import { useRef, useCallback } from 'react';
import * as React from 'react';
import { SceneInfo, UpdateSceneParams } from '../../../../types/scene.types';
import { useDataCache } from '../../../../contexts/BreakdownDataCacheContext';

/**
 * 场次更新 Hook
 * 
 * 功能：
 * - 处理场次信息的字段更新
 * - 编辑时只更新本地状态，不更新缓存
 * - 完成编辑时（失焦）更新缓存
 */
export const useSceneUpdate = (
  sceneInfo: SceneInfo | null,
  setSceneInfo: React.Dispatch<React.SetStateAction<SceneInfo | null>>,
  sceneId: string,
  onSceneUpdate?: (sceneId: string, updatedScene: SceneInfo) => void
) => {
  const { getBreakdownCache, setBreakdownCache } = useDataCache();
  
  // 待提交的更新队列
  const pendingSceneUpdatesRef = useRef<Partial<UpdateSceneParams>>({});

  const handleSceneUpdate = useCallback((field: string, value: any) => {
    if (!sceneInfo) return;

    // 本地乐观更新
    setSceneInfo(prev => prev ? { ...prev, [field]: value } : null);

    // 立即通知父组件进行乐观更新，实现实时同步
    if (onSceneUpdate) {
      onSceneUpdate(sceneId, { [field]: value } as any);
    }

    // 合并到待提交的变更集中（不更新缓存，等待完成编辑时更新）
    (pendingSceneUpdatesRef.current as any)[field] = value;
  }, [sceneInfo, setSceneInfo, sceneId, onSceneUpdate]);

  /**
   * 完成编辑时提交待提交的更新（用于失焦时）
   */
  const flushSceneUpdate = useCallback(async () => {
    const payload = pendingSceneUpdatesRef.current;
    if (!payload || Object.keys(payload).length === 0) return;
    
    // 只更新缓存，由缓存自动触发 API
    if (sceneInfo?.sceneBelongBreakdownID) {
      const breakdownId = sceneInfo.sceneBelongBreakdownID;
      const cache = getBreakdownCache(breakdownId);
      if (cache) {
        const updatedScenes = cache.scenes.map((scene) =>
          scene.sceneID === sceneId ? { ...scene, ...payload } : scene
        );
        setBreakdownCache(breakdownId, {
          scenes: updatedScenes,
        });
      }
    }
    
    pendingSceneUpdatesRef.current = {};
  }, [sceneInfo, sceneId, getBreakdownCache, setBreakdownCache]);

  return {
    handleSceneUpdate,
    flushSceneUpdate,
  };
};

